import { createContext, DependencyList, Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react';
import logger from '@gral/log';

import App from '@gral/app';
import { useAppDispatch, useAppSelector } from '@redux/store';

import Error403 from '@components/Errors/403';

import WrapperMapView from '../1_modules/newMap/WrapperMapView';
import { globalActions } from '@redux/slices/states/global/globalSlice';

import { IMapNeeds, TMapHandle } from '@components/Maps/Map/OlMap';
import useEffectAfterMount from '@utils/hooks/useEffectAfterMount';
import { EAclAction, CView, CUnitFormatter, defaultTMapLabel, CMapLabel, CLocation, CStatusIndicator } from '@ts/ui-models';
import { checkViewErrors } from '@components/Maps/utils/map.utils';
import { fetchRulesetData, fetchView, getBatchElementPositions, putPropertiesView } from '@services/DataBase/Map.service';
import { useNavigate } from 'react-router';
import { useRouterContext } from '@utils/context/RouterContext';
import { CMapLayer } from '@models/maps/Layer';
import { IRuleset, TRecordItems, TRecordLabels, TRecordLocation } from '@interfaces/maps/Map.interface';
import { CMapElement, CMapObserver } from '@models/maps/Map';

const log = logger(`MapPage`);

export interface ICycleMapState {
  fetchedView: boolean;
  fetchedRulesets: boolean; //We need the view for find the rulesets
  fetchedMapNeeds: boolean; //From the ruleset we get Layers, labels, elements
  mountedMap: boolean; //First we mount empty map
  mountedCompleteMap: boolean; //After mount map with all layers
  allTimelinePositionsFetched: number; //Increment when user change timeline
  reSyncData: number; //Increment when data need to be re-synced
}
interface IMapContext {
  mapNeeds: IMapNeeds;
  view: CView;
  setView: (view: CView) => void;
  cycleState: ICycleMapState;
  setCycleState: Dispatch<SetStateAction<ICycleMapState>>;
  mapObserver: CMapObserver;
}
const MapContext = createContext<IMapContext | null>(null);

const MapPage = () => {
  const { transferPropsRoute } = useRouterContext();
  const { id: viewID } = transferPropsRoute.params;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const mapRef = useRef<TMapHandle>(null);

  const mapObserver = useRef(new CMapObserver()).current;

  const readingTypeList = useAppSelector(state => state.global.readingTypesList);
  const unitsList = useAppSelector(state => state.global.unitsList);
  const unitFormatter = new CUnitFormatter(unitsList, readingTypeList);

  const [view, setView] = useState<CView | null>(null);
  const [layers, setLayers] = useState<CMapLayer[]>([]);
  const [labels, setLabels] = useState<TRecordLabels>({});
  const [locations, setLocations] = useState<TRecordLocation | null>(null);
  const tMapItemsRef = useRef<TRecordItems>({});
  const tMapItems = tMapItemsRef.current;

  const [elementIDFindTimeline, setElementIDFindTimeline] = useState<string[] | null>(null);

  const [state, setState] = useState({
    onClickSave: false,
    onClickRefreshMap: 0,
  });
  const [cycleState, setCycleState] = useState<ICycleMapState>({
    fetchedView: false,
    fetchedRulesets: false,
    fetchedMapNeeds: false,
    mountedMap: false,
    mountedCompleteMap: false,
    allTimelinePositionsFetched: 0,
    reSyncData: 0,
  });

  //#region Handlers
  /*   const onFindTimelinePosition = (elementID: string) => {
    setElementIDFindTimeline(elementID);
  }; */
  //#endregion

  //#region Functions

  const fetchRulesets = async (view: any) => {
    log.info(1, 'Fetching Rulesets');
    const constructRulesetQuery = (layerId: string, rulesets: any[], coordinates: any) => {
      return {
        data: {
          dataPath: `layer_${layerId}`,
          ruleset: rulesets,
          targetEntity: 'layer',
          targetId: layerId,
          viewTypeCoordinates: coordinates,
          refreshPeriod: 1,
        },
      };
    };

    const getRulesetsForLayer = (layer: any) => {
      return layer.xLayerRulesets.map((xLR: any) => ({
        logicalId: xLR.rulesetId,
        priority: xLR.priority,
      }));
    };
    const queries =
      view.xViewLayers
        ?.filter(xvl => !!xvl.layer)
        .map(xVL => {
          const rulesets = getRulesetsForLayer(xVL.layer);
          return constructRulesetQuery(xVL.layerId, rulesets, view.viewType.coordinates);
        }) ?? [];

    return await fetchRulesetData(dispatch, queries);
  };

  const generateMapLayers = (view: CView, rulesets: IRuleset[]) => {
    log.info(3, 'Generating map layers from rulesets');
    const layers: CMapLayer[] = [];
    const allLocations: TRecordLocation = {};
    const allItems: TRecordItems = {};
    const allLabels: TRecordLabels = {};

    for (let ruleset of rulesets) {
      if (!ruleset) continue;
      const { targetId, itemsIds, items, locations = null, rulesetActions } = ruleset;
      const tMapLayer: CMapLayer | undefined = view?.xViewLayers
        ?.filter(xvl => !!xvl.layer)
        ?.map(xVL => new CMapLayer(xVL))
        .find(xVL => xVL.layerId === targetId);
      if (!tMapLayer) continue;
      tMapLayer.itemIDs = itemsIds ?? [];

      tMapLayer.geometriesIds = locations?.map(geo => geo.logicalId) ?? [];
      tMapLayer.itemActions = rulesetActions;
      tMapLayer && layers.push(tMapLayer);

      locations?.forEach(loc => {
        allLocations[loc.logicalId] = new CLocation(loc);
      });
      items.forEach(x => {
        const tMapElement = new CMapElement(x, unitFormatter);
        if (x.xItemTags && x.xItemTags.length > 0) {
          x.xItemTags.forEach(xItemTag => {
            const rLabel = allLabels[xItemTag.tag.logicalId];
            if (!rLabel) {
              const nLabel = new CMapLabel(xItemTag.tag);
              nLabel.itemIDs.push(tMapElement.getID());
              allLabels[nLabel.getID()] = nLabel;
            } else {
              rLabel.itemIDs.push(tMapElement.getID());
            }
          });
        } else {
          const withoutLabel = defaultTMapLabel;
          withoutLabel.itemIDs.push(tMapElement.getID());
          allLabels['without'] = withoutLabel;
        }
        tMapElement.layerIDs = layers.filter(l => l.itemIDs.includes(tMapElement.getID())).map(l => l.getID()) ?? [];
        tMapElement.rulesetAction = layers
          .find(l => l.getID() === tMapElement.layerIDs[0])
          ?.getInfoBubbleFromElementID(tMapElement.getID());
        allItems[tMapElement.getID()] = tMapElement;
      });
    }

    setLocations(allLocations);

    //setTMapItems(allItems);
    tMapItemsRef.current = allItems;
    setLabels(allLabels);

    layers.push(CMapLayer.default(view)); //Layer for a new element searched
    setLayers(layers);
    setCycleState(prevState => ({ ...prevState, fetchedMapNeeds: true }));
  };
  //#endregion

  const useEffect01MountMapPageAndFetchData = (deps: DependencyList) => {
    useEffect(() => {
      const fetchInitialData = async () => {
        log.info(0, 'Fetching ViewMap');
        const view = await fetchView(dispatch, viewID);
        setView(view);
        setCycleState(prevState => ({ ...prevState, fetchedView: true }));
        if (view) {
          const rulesets = await fetchRulesets(view);
          generateMapLayers(view, rulesets);
          setCycleState(prevState => ({ ...prevState, fetchedRulesets: true }));
        }
      };
      fetchInitialData();
      return () => {
        log.info('Dismount MapPage');
        Object.values(tMapItemsRef.current).forEach(item => {
          item.disconnectMQTT();
        });
        tMapItemsRef.current = {};
        setCycleState(prevState => ({ ...prevState, fetchedView: false, fetchedRulesets: false }));
      };
    }, deps);
  };

  const useEffect02FetchElementPositions = (deps: DependencyList) => {
    useEffectAfterMount(() => {
      let isCancelled = false; // Flag to cancel

      if (
        cycleState.fetchedView &&
        cycleState.mountedMap &&
        view &&
        Object.keys(tMapItems).length > 0 &&
        !elementIDFindTimeline
      ) {
        log.info(7, 'Updating all element position', view);

        const getPositions = async () => {
          await getBatchElementPositions(
            dispatch,
            Object.keys(tMapItems),
            tMapItems,
            view,
            !Boolean(elementIDFindTimeline),
            () => isCancelled, // Pasamos la referencia de la cancelación
            1 //Only get last position
          );
          if (!isCancelled) {
            setCycleState(prevState => ({
              ...prevState,
              allTimelinePositionsFetched: prevState.allTimelinePositionsFetched + 1,
              reSyncData: 0,
            }));
          }
        };

        getPositions();
      }

      // Cleanup para establecer la bandera cuando el efecto se limpia
      return () => {
        isCancelled = true;
      };
    }, deps);
  };

  const useEffectFetchNewElementPositions = (deps: DependencyList) => {
    useEffectAfterMount(() => {
      if (view && cycleState.mountedMap && Object.keys(tMapItems).length > 0 && elementIDFindTimeline) {
        log.info('Updating new element position', view);
        const getPositions = async () => {
          await getBatchElementPositions(
            dispatch,
            elementIDFindTimeline,
            tMapItems,
            view,
            !Boolean(elementIDFindTimeline),
            () => false
          );
          setCycleState(prevState => ({
            ...prevState,
            allTimelinePositionsFetched: prevState.allTimelinePositionsFetched + 1,
          }));
          //setElementIDFindTimeline(null);
        };
        getPositions();
      }
    }, deps);
  };

  const useEffectSaveMapConfiguration = (deps: DependencyList) => {
    useEffectAfterMount(() => {
      if (state.onClickSave && view) {
        log.info('Updating View properties', view);
        putPropertiesView(dispatch, viewID, view.properties);
        setState({ ...state, onClickSave: false });
      }
    }, deps);
  };

  useEffect01MountMapPageAndFetchData([]);
  useEffect02FetchElementPositions([cycleState.mountedCompleteMap, state.onClickRefreshMap, elementIDFindTimeline]);
  //useEffect03UpdateMapElementsWithStatuses([items]);

  useEffectFetchNewElementPositions([state.onClickRefreshMap, elementIDFindTimeline]);
  useEffectSaveMapConfiguration([state.onClickSave]);

  const viewErrors = useMemo(() => {
    return checkViewErrors(view);
  }, [view]);

  if (!App.cxStore.checkPermission('taView', EAclAction.READ)) return <Error403 />;
  if (!view) return;
  if (viewErrors) {
    dispatch(globalActions.notification(viewErrors.toString()));
    navigate(-1);
    return;
  }

  const mapNeeds: IMapNeeds = {
    layers,
    items: tMapItems,
    labels,
    locations,
  };

  return (
    <MapContext.Provider value={{ mapNeeds, view, setView, cycleState, setCycleState, mapObserver }}>
      <WrapperMapView
        ref={mapRef}
        mapNeeds={mapNeeds}
        inheritedMapProps={{
          onClickBtnSave: () => setState({ ...state, onClickSave: true }),
          onRefreshMap: () => setState({ ...state, onClickRefreshMap: state.onClickRefreshMap + 1 }),
          onFindTimelinePosition: setElementIDFindTimeline,
        }}
      />
    </MapContext.Provider>
  );
};

export default MapPage;

// Hook to use MapContext in child components
export const useMapContext = (): IMapContext | null => {
  const context = useContext(MapContext);
  return context ?? null;
};
